// src/api/annuaire.api.ts — SCE Issues
// L'annuaire de l'entreprise : chercher une personne, la retrouver d'après son
// nom, afficher sa photo. Lecture seule. Sert au choix du leader, de l'expert
// et des responsables d'actions FNC-T (LeaderUPN / ResponsableUPN), puis aux
// pastilles de toute l'appli (backlog « Photos partout »).
//
// Ce qui marche (essais de /admin, 07/10, PC et iPhone) :
//  - la recherche : le sélecteur de personnes de SharePoint REST (même jeton
//    qu'ensureUser), nom, adresse, service ;
//  - le profil de chaque résultat : `PeopleManager/GetPropertiesFor` (Office
//    = `NOVA_Pompey`, FirstName, LastName, Department, Title, PictureUrl) ;
//  - la photo : le fichier « User Photos » du profil, lu PAR GRAPH (adresse
//    de téléchargement pré-authentifiée). Jamais de `fetch` avec jeton vers
//    `_layouts` ou `-my` (CORS), jamais de <img> vers SharePoint (iPhone :
//    refusée, ou silhouette par défaut).
//
// Tout est gardé pour la session (profils, personnes retrouvées, photos) :
// une pastille affichée vingt fois ne coûte qu'une lecture. Règles : utils/annuaire.

import { sharepointGet, sharepointPost } from './sharepointClient'
import { graphGet, graphPost } from './graphClient'
import { attendreReseauCalme } from './activiteReseau'
import {
  completerPersonne,
  lirePersonnesAnnuaire,
  lireRecherchePersonnes,
  partagerParSite,
  PROPRIETES_RECHERCHE,
  requeteNomKql,
  requeteNomPompeyKql,
  SOURCE_PERSONNES,
  lireProprietesProfil,
  personneDepuisProfil,
  nomDeFamille,
  retrouverParNomDetail,
  type RaisonNom,
  type Personne,
  type ProprieteProfil,
} from '@/utils/annuaire'

const SITE_PATH = '/sites/OP_SCE'

// ─── Profils ───

const profils = new Map<string, Promise<ProprieteProfil[] | null>>()

/** Les champs remplis du profil ; null s'il est illisible (jamais d'exception). */
function lireProfil(upn: string): Promise<ProprieteProfil[] | null> {
  const cle = upn.toLowerCase()
  let p = profils.get(cle)
  if (!p) {
    const compte = `i:0#.f|membership|${cle}`
    p = sharepointGet<unknown>(
      SITE_PATH,
      `SP.UserProfiles.PeopleManager/GetPropertiesFor(accountName=@v)?@v='${encodeURIComponent(compte)}'`,
    )
      .then((brut) => lireProprietesProfil(brut))
      .catch((err: unknown) => {
        console.warn(`[annuaire] profil illisible (${cle}) :`, err)
        return null
      })
    profils.set(cle, p)
  }
  return p
}

// ─── Recherche ───

/**
 * Les personnes dont le nom commence par `texte` (2 lettres au moins),
 * complétées par leur profil — on attend les profils avant de répondre, pour
 * ne pas réordonner la liste sous les yeux. PrincipalSource 15 = toutes les
 * sources ; PrincipalType 1 = utilisateurs. Les invités (B2B, externes) sont
 * écartés, sauf `invites` (« Envoyer au BE », 22/10) : ils n'existent dans la
 * recherche que si le tenant les y montre.
 */
export async function rechercherPersonnes(texte: string, nombre = 20, { invites = false }: { invites?: boolean } = {}): Promise<Personne[]> {
  const q = texte.trim()
  if (q.length < 2) return []
  const reponse = await sharepointPost<{ ClientPeoplePickerSearchUser?: string }>(
    SITE_PATH,
    'SP.UI.ApplicationPages.ClientPeoplePickerWebServiceInterface.clientPeoplePickerSearchUser',
    {
      queryParams: {
        __metadata: { type: 'SP.UI.ApplicationPages.ClientPeoplePickerQueryParameters' },
        AllowEmailAddresses: false,
        AllowMultipleEntities: false,
        AllUrlZones: false,
        MaximumEntitySuggestions: nombre,
        PrincipalSource: 15,
        PrincipalType: 1,
        QueryString: q,
      },
    },
  )
  const trouves = lirePersonnesAnnuaire(reponse?.ClientPeoplePickerSearchUser).filter((p) => invites || !p.invite)
  const personnes = await Promise.all(trouves.map(async (p) => completerPersonne(p, await lireProfil(p.upn))))
  // Les photos partent tout de suite (en un paquet), sans attendre que la liste les demande.
  for (const p of personnes) if (p.photo) void photoDe(p.upn)
  return personnes
}

async function rechercheSharePoint(kql: string, nombre: number): Promise<Personne[]> {
  const proprietes = PROPRIETES_RECHERCHE.join(',')
  const brut = await sharepointGet<unknown>(
    SITE_PATH,
    `search/query?querytext='${encodeURIComponent(kql.replace(/'/g, "''"))}'&sourceid='${SOURCE_PERSONNES}'&rowlimit=${nombre}&selectproperties='${proprietes}'&trimduplicates=false`,
  )
  return lireRecherchePersonnes(brut)
}

/**
 * Les personnes DE POMPEY dont le nom commence par `texte` (2 lettres au moins) —
 * « Gui », « Guillaume », « del », « guillaume del ». Par la recherche de
 * personnes de SharePoint (utils/annuaire) : filtrée sur Pompey dans la requête ;
 * si l'index ne connaît pas le bureau, par le nom seul puis le bureau de chacun
 * (profil lu pour ceux que l'index ne situe pas). Repli sur le sélecteur de
 * personnes, plus large qu'avant, si la recherche ne répond pas.
 */
export async function rechercherPompey(texte: string, nombre = 20): Promise<Personne[]> {
  const q = texte.trim()
  if (q.length < 2) return []
  try {
    const filtree = (await rechercheSharePoint(requeteNomPompeyKql(q), nombre)).filter((p) => p.pompey !== false)
    if (filtree.length > 0) return avecPhotos(await situer(filtree, nombre))
    const parNomSeul = await rechercheSharePoint(requeteNomKql(q), 200)
    if (parNomSeul.length > 0) return avecPhotos(await situer(parNomSeul, nombre))
  } catch (err) {
    console.warn('[annuaire] recherche de personnes indisponible, repli sur le sélecteur :', err)
  }
  return partagerParSite(await rechercherPersonnes(q, 50)).pompey.slice(0, nombre)
}

/** Ceux que l'index ne situe pas : leur profil dit s'ils sont de Pompey (20 au plus). */
async function situer(personnes: readonly Personne[], nombre: number): Promise<Personne[]> {
  const sures = personnes.filter((p) => p.pompey === true)
  const douteuses = personnes.filter((p) => p.pompey === null).slice(0, 20)
  const situees = await Promise.all(douteuses.map(async (p) => ({ ...p, pompey: completerPersonne({ ...p, invite: false }, await lireProfil(p.upn)).pompey })))
  return [...sures, ...situees.filter((p) => p.pompey !== false)].slice(0, nombre)
}

function avecPhotos(personnes: Personne[]): Personne[] {
  for (const p of personnes) if (p.photo) void photoDe(p.upn)
  return personnes
}

/** Une personne déjà choisie (son UPN et son nom), complétée par son profil. */
export async function personneDe(upn: string, nom: string): Promise<Personne> {
  return personneDepuisProfil(upn, nom, await lireProfil(upn))
}

const parNom = new Map<string, Promise<PersonneRetrouvee>>()

/**
 * La personne de l'annuaire qui porte ce « Prénom Nom », s'il n'y en a qu'une
 * (de Pompey si plusieurs) ; null sinon, ou si l'annuaire ne répond pas.
 * Pour les noms pré-remplis (coordinateur, modèle P&ID / PPL, déclarant), les
 * habitués et les noms repris de l'Excel.
 */
export function retrouverPersonne(nom: string): Promise<Personne | null> {
  return chercherParNom(nom).then((r) => r.personne)
}

export interface PersonneRetrouvee {
  personne: Personne | null
  /** `erreur` : l'annuaire n'a pas répondu (rien n'est gardé : on réessaiera). */
  raison: RaisonNom | 'erreur'
}

/**
 * Retrouver un « Prénom Nom » en disant pourquoi on n'y arrive pas. Le sélecteur
 * de SharePoint ne trouve pas toujours le nom complet (l'annuaire écrit « Nom,
 * Prénom ») : sans résultat, on cherche aussi par le nom de famille. Gardé pour
 * la session, SAUF un échec de l'annuaire (réessayé à la prochaine demande).
 */
export function chercherParNom(nom: string): Promise<PersonneRetrouvee> {
  const cle = nom.trim().toLowerCase()
  if (!cle) return Promise.resolve({ personne: null, raison: 'absent' })
  let p = parNom.get(cle)
  if (!p) {
    p = (async (): Promise<PersonneRetrouvee> => {
      const complet = await rechercherPersonnes(nom.trim())
      const r = retrouverParNomDetail(nom, complet)
      const famille = nomDeFamille(nom)
      if (r.raison !== 'absent' || famille.length < 2) return r
      return retrouverParNomDetail(nom, [...complet, ...(await rechercherPersonnes(famille))])
    })().catch((err: unknown) => {
      console.warn(`[annuaire] « ${nom} » non cherché :`, err)
      parNom.delete(cle)
      return { personne: null, raison: 'erreur' as const }
    })
    parNom.set(cle, p)
  }
  return p
}

/** L'adresse (UPN) d'un « Prénom Nom », vide si on ne la retrouve pas à coup sûr. */
export async function adresseDe(nom: string): Promise<string> {
  return (await retrouverPersonne(nom))?.upn ?? ''
}

// ─── Photos ───

interface ElementFichier {
  id: string
  '@microsoft.graph.downloadUrl'?: string
}

/** Bibliothèque de chaque hôte de profils (`sartorius-my…/User Photos`), trouvée une fois. */
const bibliotheques = new Map<string, Promise<string | null>>()

function bibliothequeDe(hote: string, nomBibliotheque: string): Promise<string | null> {
  const cle = `${hote}/${nomBibliotheque}`.toLowerCase()
  let p = bibliotheques.get(cle)
  if (!p) {
    p = (async () => {
      const site = await graphGet<{ id: string }>(`/sites/${hote}?$select=id`)
      const drives = await graphGet<{ value: { id: string; webUrl?: string }[] }>(`/sites/${site.id}/drives?$select=id,webUrl`)
      const voulu = `/${nomBibliotheque}`.toLowerCase()
      return drives.value.find((d) => d.webUrl && decodeURIComponent(new URL(d.webUrl).pathname).toLowerCase() === voulu)?.id ?? null
    })().catch(() => null)
    bibliotheques.set(cle, p)
  }
  return p
}

// Les fiches des fichiers photo sont demandées à Graph par PAQUETS ($batch,
// 20 au plus) : une liste de 20 personnes = 1 requête au lieu de 20 (la photo
// n'arrivait dans la liste qu'une fois la personne choisie, 07/10).
const attente: { url: string; resoudre: (e: ElementFichier | null) => void }[] = []
let minuterie: number | null = null

function elementPhoto(url: string): Promise<ElementFichier | null> {
  return new Promise((resoudre) => {
    attente.push({ url, resoudre })
    minuterie ??= window.setTimeout(() => {
      minuterie = null
      void viderAttente()
    }, 20)
  })
}

async function viderAttente(): Promise<void> {
  while (attente.length) {
    const paquet = attente.splice(0, 20)
    try {
      const reponse = await graphPost<{ responses?: { id: string; status: number; body?: ElementFichier }[] }>('/$batch', {
        requests: paquet.map((d, i) => ({ id: String(i), method: 'GET', url: d.url })),
      })
      const parId = new Map((reponse.responses ?? []).map((r) => [r.id, r]))
      paquet.forEach((d, i) => {
        const r = parId.get(String(i))
        d.resoudre(r && r.status >= 200 && r.status < 300 && r.body ? r.body : null)
      })
    } catch {
      paquet.forEach((d) => d.resoudre(null))
    }
  }
}

const photos = new Map<string, Promise<string | null>>()

/**
 * L'image de la photo de profil (adresse locale `blob:`), ou null : pas de
 * photo, ou illisible. Jamais d'exception : la pastille garde ses initiales.
 * Gardée pour la session (jamais libérée : quelques ko par personne).
 */
export function photoDe(upn: string): Promise<string | null> {
  const cle = upn.trim().toLowerCase()
  if (!cle) return Promise.resolve(null)
  let p = photos.get(cle)
  if (!p) {
    p = (async () => {
      // Pas prioritaire (26/10, les photos sont partout) : la page charge d'abord
      // ses listes, les photos partent quand le réseau est calme. Le calme
      // demandé (500 ms) dépasse celui du voile d'ouverture (400 ms) : les
      // photos ne le retiennent pas.
      await attendreReseauCalme(500, 10_000)
      const props = await lireProfil(cle)
      const adresse = props?.find((x) => /^pictureurl$/i.test(x.cle))?.valeur
      if (!adresse) {
        // Pas de photo sous CETTE adresse (27/10) : l'auteur d'un événement est
        // connu par son adresse MAIL, qui peut différer de son identifiant de
        // connexion (« prenom.nom@ » contre « prenom.nom1@ »). L'annuaire, cherché
        // par l'adresse, donne le vrai identifiant : on réessaie avec lui.
        const trouve = (await rechercherPersonnes(cle, 1).catch(() => []))[0]
        const vrai = trouve?.upn.trim().toLowerCase()
        return vrai && vrai !== cle ? photoDe(vrai) : null
      }
      const url = new URL(adresse)
      const [bibliotheque, ...chemin] = decodeURIComponent(url.pathname).split('/').filter(Boolean)
      if (!bibliotheque || chemin.length === 0) return null
      const drive = await bibliothequeDe(url.hostname, bibliotheque)
      if (!drive) return null
      const element = await elementPhoto(`/drives/${drive}/root:/${chemin.map(encodeURIComponent).join('/')}`)
      const telechargement = element?.['@microsoft.graph.downloadUrl']
      if (!telechargement) return null
      const r = await fetch(telechargement)
      if (!r.ok || !(r.headers.get('Content-Type') ?? '').startsWith('image/')) return null
      return URL.createObjectURL(await r.blob())
    })().catch(() => null)
    photos.set(cle, p)
  }
  return p
}
